// Operator cross-org mailbox reads + mutations — mirror OperatorMailService /
// GET /operator/mail-accounts (+ the org drill-down) and the rotate/quota/status/delete
// routes. Success bodies are Zod-validated (ContractError on drift), like operator/orgs.ts.
//
// Shapes mirror the API exactly:
//   - OperatorMailAccountView  → api/src/services/operator-mail-service.ts (list rows: + orgName + usedBytes)
//   - MailAccountUsageView     → same file (org drill-down: + usedBytes, no orgName)
//   - MailAccountSummary       → api/src/services/mail-account-store.ts (quota/status return: metadata only)
import { z } from "zod";
import { operatorFetch } from "./client";

export type MailAccountStatus = "active" | "suspended";

/** Mailbox metadata (no orgName, no usage) — returned by the quota/status mutations. */
export interface OperatorMailAccountSummary {
  id: string;
  orgId: string;
  channelId: string | null;
  channelName: string | null;
  peerId: string;
  address: string;
  stalwartAccountId: string;
  status: MailAccountStatus;
  quotaBytes: number;
  createdAt: string;
  rotatedAt: string | null;
}

/** A single-org mailbox row (operator drill-down): metadata + live disk usage. */
export interface OperatorOrgMailbox extends OperatorMailAccountSummary {
  /** Live disk usage; `null` when the mail box is unreachable. */
  usedBytes: number | null;
}

/** A cross-org mailbox row (operator list): metadata + owning org name + live usage. */
export interface OperatorMailbox extends OperatorOrgMailbox {
  orgName: string;
}

// Shared field shape reused by all three views (drift fails loud at the boundary).
const summaryShape = {
  id: z.string(),
  orgId: z.string(),
  channelId: z.string().nullable(),
  channelName: z.string().nullable(),
  peerId: z.string(),
  address: z.string(),
  stalwartAccountId: z.string(),
  status: z.enum(["active", "suspended"]),
  quotaBytes: z.number(),
  createdAt: z.string(),
  rotatedAt: z.string().nullable(),
};
const summarySchema = z.object(summaryShape) satisfies z.ZodType<OperatorMailAccountSummary>;
const orgMailboxSchema = z.object({ ...summaryShape, usedBytes: z.number().nullable() }) satisfies z.ZodType<OperatorOrgMailbox>;
const mailboxSchema = z.object({ ...summaryShape, orgName: z.string(), usedBytes: z.number().nullable() }) satisfies z.ZodType<OperatorMailbox>;

export interface OperatorMailboxPage {
  rows: OperatorMailbox[];
  nextCursor: string | null;
}
const pageSchema = z.object({
  rows: z.array(mailboxSchema),
  nextCursor: z.string().nullable(),
}) satisfies z.ZodType<OperatorMailboxPage>;

const orgListSchema = z.object({ mailboxes: z.array(orgMailboxSchema) });
const rotateSchema = z.object({ password: z.string() });

export interface ListOperatorMailboxesOptions {
  /** ILIKEs address / peer id (server-side). */
  q?: string;
  /** Exact owning-org filter. */
  orgId?: string;
  status?: MailAccountStatus;
  /** Keyset cursor — `"<createdAt>__<id>"` (a page's `nextCursor`). */
  before?: string;
  limit?: number;
}

/** Cross-org keyset page of mailboxes (each enriched with live disk usage). */
export function listOperatorMailboxes(opts: ListOperatorMailboxesOptions = {}, signal?: AbortSignal): Promise<OperatorMailboxPage> {
  const p = new URLSearchParams();
  if (opts.q) p.set("q", opts.q);
  if (opts.orgId) p.set("orgId", opts.orgId);
  if (opts.status) p.set("status", opts.status);
  if (opts.before) p.set("before", opts.before);
  if (opts.limit) p.set("limit", String(opts.limit));
  const qs = p.toString();
  return operatorFetch(`/operator/mail-accounts${qs ? `?${qs}` : ""}`, { signal, schema: pageSchema });
}

/** One org's mailboxes (operator drill-down), enriched with usage. */
export async function listOperatorOrgMailboxes(orgId: string, signal?: AbortSignal): Promise<OperatorOrgMailbox[]> {
  const res = await operatorFetch(`/operator/orgs/${encodeURIComponent(orgId)}/mail-accounts`, { signal, schema: orgListSchema });
  return res.mailboxes;
}

/** Rotate a tenant mailbox's password. `reason` (min 3 chars) is REQUIRED + audited; the new password is shown ONCE. */
export function rotateOperatorMailbox(id: string, reason: string): Promise<{ password: string }> {
  return operatorFetch(`/operator/mail-accounts/${encodeURIComponent(id)}/rotate`, { method: "POST", body: { reason }, schema: rotateSchema });
}

/** Change a tenant mailbox's quota (1 MB .. 10 GB). Returns the refreshed metadata row. */
export function setOperatorMailboxQuota(id: string, quotaBytes: number): Promise<OperatorMailAccountSummary> {
  return operatorFetch(`/operator/mail-accounts/${encodeURIComponent(id)}/quota`, { method: "PATCH", body: { quotaBytes }, schema: summarySchema });
}

/** Suspend / resume a tenant mailbox. Returns the refreshed metadata row. */
export function setOperatorMailboxStatus(id: string, status: MailAccountStatus): Promise<OperatorMailAccountSummary> {
  return operatorFetch(`/operator/mail-accounts/${encodeURIComponent(id)}/status`, { method: "PATCH", body: { status }, schema: summarySchema });
}

/** Delete a tenant mailbox (mail box + row). */
export function deleteOperatorMailbox(id: string): Promise<void> {
  return operatorFetch<void>(`/operator/mail-accounts/${encodeURIComponent(id)}`, { method: "DELETE" });
}
